import { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import {
  APPLICATIONS_LIST_SORT_VERSION,
  captureQueueSlice,
  createDefaultQueueSlice,
  loadQueueSlicesFromPersist,
} from '@/lib/applicationQueueListState';
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSortableTable } from '@/components/shared/SortableTable';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Loader2, XCircle } from 'lucide-react';
import { ResponsiveTable, TABLE_COL, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import { statusCellClass } from '@/components/ui/responsive-table/columnLayout';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { FloatingAiChat } from '@/components/shared/FloatingAiChat';
import IdentityMismatchBadge from '@/components/applications/IdentityMismatchBadge';
import {
  ApplicationRowProcessBadge,
  ScoreBadge,
  ChannelBadge,
  InterviewSubstatusBadges,
  getInterviewSortKey,
} from '@/components/shared/Badges';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { dateTimeTooltip, formatRelative } from '@/lib/dateUtils';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useApplicationsPageData } from '@/hooks/useApplicationsPageData';
import { useViewportLockedListPage } from '@/hooks/use-mobile';
import { NoCompanyEmptyState } from '@/components/shared/NoCompanyEmptyState';
import { toast } from 'sonner';
import { api } from '@/services/api';
import { emitBillingUsageChanged } from '@/lib/billingEvents';
import { usePendingBillingStore } from '@/stores/pendingBillingStore';
import ProcessPendingConfirmDialog from '@/components/billing/ProcessPendingConfirmDialog';
import { ManualApplicationDialog } from '@/components/applications/ManualApplicationDialog';
import { ListPagination, DEFAULT_LIST_PAGE_SIZE } from '@/components/shared/ListPagination';
import { ListTablePanel } from '@/components/shared/ListTablePanel';
import { cn } from '@/lib/utils';
import { isActiveCountEndpointUnavailable } from '@/lib/activeCountAvailability';
import {
  isApplicationAwaitingUpgrade,
  isApplicationReadyToProcess,
  matchesApplicationStatusFilter,
  getBulkProcessBreakdown,
  bulkProcessableCount,
  canProcessPendingApplications,
  getBulkProcessButtonState,
  reconcileDeferredCountsWithApplications,
} from '@/lib/pendingProcessing';
import {
  getAutoSelectableApplications,
  getSelectedProcessableApps,
  useShiftClickSelection,
  SelectAllHeaderCheckbox,
} from '@/lib/bulkProcessApplications';
import { useApplicationProcessQueue } from '@/hooks/useApplicationProcessQueue';
import { usePipelineStatusPoll } from '@/hooks/usePipelineStatusPoll';
import { useApplicationWatchSetRegistration } from '@/hooks/useApplicationWatchSetRegistration';
import { useBulkProcessBannerStore } from '@/stores/bulkProcessBannerStore';
import { usePersistedListView, useSkipInitialEffect } from '@/hooks/usePersistedListView';
import { useTrialWriteGuard } from '@/hooks/useTrialWriteAccess';
import {
  applyListPageToSearchParams,
  applyStatusToSearchParams,
  buildListReturnPath,
  isDocumentReload,
  isListViewLandingAfterReload,
  listViewStorageKey,
  loadSessionJson,
  parseListPageParam,
  type ApplicationListReturnState,
  type ApplicationsListPersist,
} from '@/lib/listViewPersistence';
import {
  getNextStatusPin,
  getPresentStatusPinCycle,
  buildApplicationDisplayOrder,
  buildProcessingPromoteIds,
} from '@/lib/applicationStateSort';
import type { Application, ApplicationQueueView, ApplicationState } from '@/types';
import { ApplicationsBulkActionBar } from '@/components/applications/ApplicationsBulkActionBar';
import { ApplicationsListFiltersToolbar } from '@/components/applications/ApplicationsListFiltersToolbar';
import {
  useActiveApplicationsCountQuery,
  useApplicationsFilteredCountQuery,
  useArchivedApplicationsCountQuery,
  useCompanyApplicationsInfiniteQuery,
} from '@/hooks/useRecruiterListQueries';
import {
  applicationMatchesServerScope,
  buildCompanyApplicationsListScope,
  COMPANY_APPLICATIONS_INITIAL_PREFETCH_PAGES,
  getApplicationListSortValue,
  isCompanyApplicationsWarmScope,
  isServerAlignedListSortColumn,
  mergeCompanyApplicationsListSources,
  orderApplicationsForServerList,
  scopeUsesServerFilters,
  type CompanyApplicationsListScope,
} from '@/lib/companyApplicationsList';
import { useCompanyApplicationsListCache } from '@/hooks/useCompanyApplicationsListCache';
import { useApplicationListSync } from '@/hooks/useApplicationListSync';
import { loadRecentSettledApplications } from '@/lib/recentSettledApplications';
import { useApplicationsListMountSync } from '@/hooks/useApplicationsListMountSync';
import { filterApplicationQueue, toBulkArchivePayload } from '@/lib/applicationArchive';
import { loadBulkProcessQueue } from '@/lib/bulkProcessQueuePersistence';
import {
  applyBulkArchiveToCache,
  applyBulkUnarchiveToCache,
  scheduleApplicationArchiveInvalidation,
  patchUnlockedDeferredApplications,
} from '@/lib/recruiterCache';

type AppRow = Application & { teamName?: string };

const ApplicationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedCompany } = useAuth();
  const { t } = useLanguage();
  const viewportLocked = useViewportLockedListPage();
  const queryClient = useQueryClient();
  const listSync = useApplicationListSync();
  const {
    positions,
    companyTeams,
    loading,
    refetchApplications,
    patchApplications,
    appendManualApplication,
    chatOpen,
    setChatOpen,
    chatMessages,
    chatInput,
    setChatInput,
    chatLoading,
    chatStreamStatuses,
    chatExpanded,
    setChatExpanded,
    sendChat,
    resetChat,
  } = useApplicationsPageData(selectedCompany?.id);
  const { data: billingStatus, isFetched: billingFetched } = useBillingStatus();
  const hasProcessingCredits = canProcessPendingApplications(billingStatus);
  const { guardWrite, writeBlocked } = useTrialWriteGuard();

  const listPersistKey = selectedCompany
    ? listViewStorageKey('applications', selectedCompany.id)
    : undefined;
  const pageReload = isDocumentReload();
  const landingAfterReload = pageReload || isListViewLandingAfterReload(navigationType);
  const savedList = listPersistKey && !landingAfterReload
    ? loadSessionJson<ApplicationsListPersist>(listPersistKey)
    : null;

  const queueSlicesRef = useRef(
    loadQueueSlicesFromPersist(savedList, savedList?.queueView, { forceDefaultSort: pageReload }),
  );
  const initialQueue = queueSlicesRef.current.queueView;
  const initialSlice = queueSlicesRef.current[initialQueue];

  const [queueView, setQueueView] = useState<ApplicationQueueView>(initialQueue);

  const [statusFilter, setStatusFilter] = useState(() => {
    const fromUrl = searchParams.get('status');
    if (fromUrl) return fromUrl;
    if (landingAfterReload) return 'all';
    return savedList?.statusFilter ?? 'all';
  });
  // Sprint 4 (S4.C) — manual application creation flow
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  // Sprint 5 (S5.D) — bulk select state. We store the selected ids in a Set
  // (not an array) so toggle is O(1) and the "select all" / "clear" buttons
  // stay snappy even with hundreds of rows.
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectNote, setBulkRejectNote] = useState('');
  const [bulkRejectSendEmail, setBulkRejectSendEmail] = useState(false);
  const [bulkActionRunning, setBulkActionRunning] = useState(false);
  const [bulkProcessConfirmOpen, setBulkProcessConfirmOpen] = useState(false);
  const setDeferredCounts = usePendingBillingStore(s => s.setDeferredCounts);
  const storeBillingRetry = usePendingBillingStore(s => s.billingRetry);

  const bulkBannerActive = useBulkProcessBannerStore((s) => s.phase === 'active');
  const bulkSessionActive = useMemo(() => {
    if (!selectedCompany?.id) return false;
    return loadBulkProcessQueue(selectedCompany.id).targetIds.length > 0;
  }, [selectedCompany?.id, bulkBannerActive]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(initialSlice.selectedIds ?? []),
  );

  // Full reload landing: drop filter query params so nothing survives refresh.
  useEffect(() => {
    if (!landingAfterReload) return;
    if (!searchParams.has('status') && !searchParams.has('selectReady') && !searchParams.has('page')) {
      return;
    }
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('status');
      next.delete('selectReady');
      next.delete('page');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on reload landing
  }, []);

  // Sync statusFilter when ?status= changes (banner → Applications; ignore ?page= alone)
  useEffect(() => {
    const s = searchParams.get('status');
    if (s && s !== statusFilter) setStatusFilter(s);
  }, [searchParams.get('status')]); // eslint-disable-line react-hooks/exhaustive-deps

  const [positionFilter, setPositionFilter] = useState(() => savedList?.positionFilter ?? 'all');
  const [teamFilter, setTeamFilter] = useState(() => savedList?.teamFilter ?? 'all');
  const [search, setSearch] = useState(() => savedList?.search ?? '');
  const [page, setPage] = useState(() => {
    if (!landingAfterReload && searchParams.get('page')) {
      return parseListPageParam(searchParams.get('page'));
    }
    return initialSlice.page;
  });

  const handlePageChange = useCallback((next: number) => {
    setPage(next);
    setSearchParams((prev) => applyListPageToSearchParams(prev, next), { replace: true });
  }, [setSearchParams]);

  const handleStatusFilterChange = useCallback((next: string) => {
    setStatusFilter(next);
    setPage(1);
    setSearchParams(
      (prev) => applyStatusToSearchParams(applyListPageToSearchParams(prev, 1), next),
      { replace: true },
    );
  }, [setSearchParams]);

  // Browser back/forward: restore page when ?page= is present (do not force 1 when param is absent).
  useEffect(() => {
    if (landingAfterReload || !searchParams.has('page')) return;
    const urlPage = parseListPageParam(searchParams.get('page'));
    if (urlPage !== page) setPage(urlPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync page from URL only
  }, [searchParams, landingAfterReload]);

  const openApplicationDetail = useCallback((row: AppRow) => {
    const listReturnPath = buildListReturnPath(location.pathname, searchParams, page);
    const detailPath = row.positionId
      ? `/job-positions/${row.positionId}/applications/${row.id}`
      : `/applications/${row.id}`;
    navigate(detailPath, { state: { listReturnPath } satisfies ApplicationListReturnState });
  }, [navigate, location.pathname, searchParams, page]);
  const isArchiveView = queueView === 'archive';

  const selectProcessableIntentRef = useRef(
    searchParams.get('selectReady') === '1'
      || (location.state as { selectProcessable?: boolean } | null)?.selectProcessable === true,
  );
  const selectReadyHandledRef = useRef(false);

  useEffect(() => {
    if (searchParams.get('selectReady') === '1') {
      selectProcessableIntentRef.current = true;
      selectReadyHandledRef.current = false;
    }
    const navState = location.state as { selectProcessable?: boolean } | null;
    if (navState?.selectProcessable) {
      selectProcessableIntentRef.current = true;
      selectReadyHandledRef.current = false;
    }
  }, [searchParams, location.state]);

  const [pinnedStatus, setPinnedStatus] = useState<ApplicationState | null>(
    () => initialSlice.pinnedStatus,
  );

  const [queueSortDefaults, setQueueSortDefaults] = useState(() => {
    if (pageReload) {
      const fresh = createDefaultQueueSlice();
      return { column: fresh.sortColumn, direction: fresh.sortDirection };
    }
    return {
      column: initialSlice.sortColumn,
      direction: initialSlice.sortDirection,
    };
  });

  const listScope = useMemo(
    () => buildCompanyApplicationsListScope({
      archived: isArchiveView,
      statusFilter,
      positionFilter,
      sortColumn: queueSortDefaults.column,
      sortDirection: queueSortDefaults.direction,
    }),
    [
      isArchiveView,
      statusFilter,
      positionFilter,
      queueSortDefaults.column,
    ],
  );

  const { applications: companyListOverlay } = useCompanyApplicationsListCache(
    selectedCompany?.id,
    isArchiveView,
  );

  const listQuery = useCompanyApplicationsInfiniteQuery(
    selectedCompany?.id,
    positions,
    listScope,
    !!selectedCompany?.id && !loading,
  );

  const recentSettledOverlay = useMemo(
    () => (selectedCompany?.id ? loadRecentSettledApplications(selectedCompany.id) : []),
    [selectedCompany?.id, companyListOverlay],
  );

  const { listOrderSettling } = useApplicationsListMountSync({
    companyId: selectedCompany?.id,
    pageReload,
    loading,
    positions,
    listScope,
  });

  /** Server pages + reactive cache overlay — survives tab navigation and stale refetch. */
  const loadedApplications = useMemo(() => {
    if (!selectedCompany?.id) return [];
    const scopedOverlay = companyListOverlay.filter((app) =>
      applicationMatchesServerScope(app, listScope),
    );
    const scopedRecent = recentSettledOverlay.filter((app) =>
      applicationMatchesServerScope(app, listScope)
      && (isArchiveView ? app.archived === true : app.archived !== true),
    );
    return mergeCompanyApplicationsListSources(
      queryClient,
      selectedCompany.id,
      listScope,
      listQuery.data?.pages,
      scopedOverlay,
      scopedRecent,
    );
  }, [
    queryClient,
    selectedCompany?.id,
    listScope,
    listQuery.data?.pages,
    companyListOverlay,
    recentSettledOverlay,
    isArchiveView,
  ]);

  const listApplications = useMemo(() => {
    const queued = filterApplicationQueue(loadedApplications, isArchiveView);
    const seen = new Set<string>();
    const deduped = queued.filter((a) => {
      if (seen.has(a.id)) return false;
      seen.add(a.id);
      return true;
    });
    if (
      isCompanyApplicationsWarmScope(listScope)
      && isServerAlignedListSortColumn(queueSortDefaults.column)
    ) {
      return orderApplicationsForServerList(deduped, listScope.orderBy);
    }
    return deduped;
  }, [loadedApplications, isArchiveView, listScope, queueSortDefaults.column]);

  const processQueue = useApplicationProcessQueue({
    companyId: selectedCompany?.id,
    applications: loadedApplications,
    refetchApplications,
    onPatchApplications: patchApplications,
    t,
  });

  usePipelineStatusPoll(
    selectedCompany?.id,
    loadedApplications,
    !!selectedCompany?.id && !isArchiveView,
  );

  const listQuerySettled = listQuery.isFetched || listQuery.isError;
  const listInitialLoad = !!selectedCompany?.id && !loading && !listQuerySettled;

  const baseListLoading =
    loading
    || listOrderSettling
    || listInitialLoad
    || ((bulkBannerActive || bulkSessionActive) && loadedApplications.length === 0 && listInitialLoad);

  const positionTeamById = useMemo(() => {
    const m: Record<string, string | undefined> = {};
    for (const p of positions) m[p.id] = p.team;
    return m;
  }, [positions]);

  const positionTitleById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of positions) m[p.id] = p.title;
    return m;
  }, [positions]);

  const statusOptions = [
    { value: 'all', label: t('applications.allStatus') },
    { value: 'APPLIED_NEW', label: t('applications.new') },
    { value: 'PENDING_UPGRADE', label: t('application.pendingUpgrade.badge') },
    { value: 'READY_TO_PROCESS', label: t('application.readyToProcess.badge') },
    { value: 'PIPELINE_RUNNING', label: t('applications.processing') },
    { value: 'PIPELINE_STUCK', label: t('applications.processingStuck') },
    { value: 'AWAITING_DECISION', label: t('applications.awaitingDecision') },
    { value: 'REJECTED', label: t('applications.rejected') },
    { value: 'HIRED', label: t('applications.hired') },
    { value: 'OFFER_DECLINED', label: t('applications.offerDeclined') },
    { value: 'PIPELINE_ERROR', label: t('applications.reviewRequired') },
    { value: 'WITHDRAWN', label: t('applications.withdrawn') },
  ];

  const activePositions = useMemo(() => positions.filter(p => p.status === 'active'), [positions]);

  const hasActiveListFilters =
    statusFilter !== 'all'
    || positionFilter !== 'all'
    || teamFilter !== 'all'
    || search.trim().length > 0;

  const hasClientOnlyListFilters =
    (teamFilter !== 'all' || search.trim().length > 0)
    && statusFilter === 'all'
    && positionFilter === 'all';

  const useFilteredCountApi =
    scopeUsesServerFilters(listScope) && !hasClientOnlyListFilters;

  const filteredCountQuery = useApplicationsFilteredCountQuery(
    selectedCompany?.id,
    listScope,
    !!selectedCompany?.id && useFilteredCountApi,
  );
  const filteredCountFromApi = filteredCountQuery.data;

  const activeCountQuery = useActiveApplicationsCountQuery(
    hasActiveListFilters ? undefined : selectedCompany?.id,
  );
  const archivedCountQuery = useArchivedApplicationsCountQuery(
    hasActiveListFilters ? undefined : selectedCompany?.id,
  );

  const activeCountFromApi = activeCountQuery.data;
  const archivedCountFromApi = archivedCountQuery.data;

  const listHasMore =
    listQuery.hasNextPage
    ?? (listQuery.isPending && loadedApplications.length > 0 && scopeUsesServerFilters(listScope));
  const listFetchingMore = listQuery.isFetchingNextPage;
  const apiPagesLoaded = listQuery.data?.pages.length ?? 0;

  const tabLoadedCount = listApplications.length;

  const activeApplicationsCount = useMemo(() => {
    if (isArchiveView) return undefined;
    if (useFilteredCountApi) {
      if (filteredCountFromApi != null) return filteredCountFromApi;
      if (filteredCountQuery.isLoading) return null;
      return tabLoadedCount > 0 ? tabLoadedCount : undefined;
    }
    if (activeCountFromApi != null) return activeCountFromApi;
    if (activeCountQuery.isLoading || activeCountQuery.isFetching) return null;
    // Legacy progressive merge inflates tabLoadedCount — wait for server count.
    if (
      selectedCompany?.id
      && isActiveCountEndpointUnavailable(selectedCompany.id)
    ) {
      return tabLoadedCount > 0 ? tabLoadedCount : undefined;
    }
    return null;
  }, [
    isArchiveView,
    useFilteredCountApi,
    filteredCountFromApi,
    filteredCountQuery.isLoading,
    tabLoadedCount,
    activeCountFromApi,
    activeCountQuery.isLoading,
    activeCountQuery.isFetching,
    selectedCompany?.id,
  ]);

  const archivedApplicationsCount = useMemo(() => {
    if (!isArchiveView) {
      if (useFilteredCountApi) return undefined;
      if (archivedCountFromApi != null) return archivedCountFromApi;
      return undefined;
    }
    if (useFilteredCountApi) {
      if (filteredCountFromApi != null) return filteredCountFromApi;
      if (filteredCountQuery.isLoading) return null;
      return tabLoadedCount > 0 ? tabLoadedCount : undefined;
    }
    if (archivedCountFromApi != null) return archivedCountFromApi;
    return tabLoadedCount > 0 ? tabLoadedCount : undefined;
  }, [
    isArchiveView,
    useFilteredCountApi,
    filteredCountFromApi,
    filteredCountQuery.isLoading,
    tabLoadedCount,
    archivedCountFromApi,
  ]);

  const activeTabCountHasMore =
    !isArchiveView
    && listHasMore
    && (useFilteredCountApi ? filteredCountFromApi == null : activeCountFromApi == null);
  const archivedTabCountHasMore =
    isArchiveView
    && listHasMore
    && (useFilteredCountApi ? filteredCountFromApi == null : archivedCountFromApi == null);

  const activeTabCountLoading =
    !isArchiveView
    && activeApplicationsCount == null
    && (useFilteredCountApi ? filteredCountQuery.isLoading : activeCountQuery.isLoading);

  const clearAllListFilters = useCallback(() => {
    setStatusFilter('all');
    setPositionFilter('all');
    setTeamFilter('all');
    setSearch('');
    setPage(1);
  }, []);

  const resolveTeamName = useCallback(
    (positionId: string) => {
      const tid = positionTeamById[positionId];
      if (!tid) return t('positions.noTeam');
      return companyTeams.find(ct => ct.id === tid)?.name ?? tid;
    },
    [positionTeamById, companyTeams, t],
  );

  const filtered = useMemo(() => {
    return listApplications.filter(a => {
      if (!matchesApplicationStatusFilter(
        a,
        statusFilter,
        hasProcessingCredits,
        { isRowProcessing: processQueue.isInProcessingFilter },
      )) {
        return false;
      }
      if (positionFilter !== 'all' && a.positionId !== positionFilter) return false;
      const tid = positionTeamById[a.positionId];
      if (teamFilter === 'unassigned') {
        if (tid) return false;
      } else if (teamFilter !== 'all' && tid !== teamFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const posTitle = (a.positionTitle || positionTitleById[a.positionId] || '').toLowerCase();
        if (!`${a.candidateFirstName} ${a.candidateLastName}`.toLowerCase().includes(q) &&
            !posTitle.includes(q)) return false;
      }
      return true;
    });
  }, [
    listApplications,
    statusFilter,
    hasProcessingCredits,
    positionFilter,
    teamFilter,
    search,
    positionTeamById,
    positionTitleById,
    processQueue.isInProcessingFilter,
  ]);

  const rowsWithTeam = useMemo(
    () => filtered.map(a => ({
      ...a,
      positionTitle: a.positionTitle || positionTitleById[a.positionId] || '—',
      teamName: resolveTeamName(a.positionId),
    })),
    [filtered, positionTitleById, resolveTeamName],
  );

  const applicationSortOptions = useMemo(() => ({
    getSortValue: (item: Application & { teamName?: string }, column: string) => {
      if (column === 'selection') return selectedIds.has(item.id) ? 0 : 1;
      if (column === 'interviewStatus') return getInterviewSortKey(item.interviews);
      return getApplicationListSortValue(item, column);
    },
  }), [selectedIds]);

  const { sorted, sort, toggleSort } = useSortableTable(
    rowsWithTeam,
    queueSortDefaults,
    {
      ...applicationSortOptions,
      resetKey: queueView,
    },
  );

  const handleQueueViewChange = useCallback((next: ApplicationQueueView) => {
    if (next === queueView) return;
    queueSlicesRef.current[queueView] = captureQueueSlice({
      page,
      pinnedStatus,
      sortColumn: sort.column ?? queueSortDefaults.column,
      sortDirection: (sort.direction ?? queueSortDefaults.direction) as 'asc' | 'desc',
      selectedIds,
    });
    const nextSlice = queueSlicesRef.current[next];
    setQueueSortDefaults({
      column: nextSlice.sortColumn,
      direction: nextSlice.sortDirection,
    });
    setQueueView(next);
    setPage(nextSlice.page);
    setSelectedIds(new Set(nextSlice.selectedIds ?? []));
    setPinnedStatus(nextSlice.pinnedStatus);
  }, [queueView, page, pinnedStatus, selectedIds, sort.column, sort.direction, queueSortDefaults.column, queueSortDefaults.direction]);

  const statusPinCycle = useMemo(
    () => getPresentStatusPinCycle(rowsWithTeam, hasProcessingCredits),
    [rowsWithTeam, hasProcessingCredits],
  );

  // Pin in-flight rows (queue slots + Processing badge) while bulk runs or session persists.
  const bulkBannerTargetIds = useBulkProcessBannerStore((s) => s.targetIds);

  const promoteIds = useMemo(
    () => buildProcessingPromoteIds({
      processingIds: processQueue.processingIds,
      stoppingIds: processQueue.stoppingIds,
      showsActiveProcessingBadge: processQueue.showsActiveProcessingBadge,
      listApplications,
      bulkBannerActive,
      bulkBannerTargetIds,
    }),
    [
      processQueue.processingIds,
      processQueue.stoppingIds,
      processQueue.showsActiveProcessingBadge,
      listApplications,
      bulkBannerActive,
      bulkBannerTargetIds,
    ],
  );

  const displaySorted = useMemo(
    () => buildApplicationDisplayOrder(sorted, { pinnedStatus, promoteIds, hasProcessingCredits }),
    [sorted, pinnedStatus, promoteIds, hasProcessingCredits],
  );

  const cycleStatusPin = useCallback(() => {
    setPinnedStatus(prev => getNextStatusPin(prev, statusPinCycle));
  }, [statusPinCycle]);

  const handleColumnSort = useCallback((column: string) => {
    setPinnedStatus(null);
    const prevColumn = sort.column ?? queueSortDefaults.column;
    const columnChanged = prevColumn !== column;
    toggleSort(column);
    if (isServerAlignedListSortColumn(column) && column !== queueSortDefaults.column) {
      setQueueSortDefaults((prev) => ({ ...prev, column }));
    }
    if (columnChanged) {
      setPage(1);
    }
  }, [toggleSort, sort.column, queueSortDefaults.column]);

  useEffect(() => {
    if (pinnedStatus && !statusPinCycle.includes(pinnedStatus)) {
      setPinnedStatus(null);
    }
  }, [pinnedStatus, statusPinCycle]);

  const filteredCount = displaySorted.length;
  const paginationTotal = useMemo(() => {
    if (useFilteredCountApi && filteredCountFromApi != null) {
      return Math.max(filteredCount, filteredCountFromApi);
    }
    if (!isArchiveView && activeCountFromApi != null) {
      return Math.max(filteredCount, activeCountFromApi);
    }
    if (isArchiveView && archivedCountFromApi != null) {
      return Math.max(filteredCount, archivedCountFromApi);
    }
    return filteredCount;
  }, [
    useFilteredCountApi,
    filteredCountFromApi,
    isArchiveView,
    filteredCount,
    activeCountFromApi,
    archivedCountFromApi,
    listHasMore,
  ]);

  const loadedTablePages = Math.max(1, Math.ceil(filteredCount / DEFAULT_LIST_PAGE_SIZE));
  const reachableTablePages =
    listHasMore || listFetchingMore ? loadedTablePages + 1 : loadedTablePages;

  const totalPages = useMemo(() => {
    const fullTotal = Math.max(1, Math.ceil(paginationTotal / DEFAULT_LIST_PAGE_SIZE));
    const hasApiTotal =
      (useFilteredCountApi && filteredCountFromApi != null)
      || (!isArchiveView && activeCountFromApi != null)
      || (isArchiveView && archivedCountFromApi != null);
    if (hasApiTotal) {
      return Math.min(fullTotal, reachableTablePages);
    }
    return reachableTablePages;
  }, [
    paginationTotal,
    useFilteredCountApi,
    filteredCountFromApi,
    isArchiveView,
    activeCountFromApi,
    archivedCountFromApi,
    reachableTablePages,
  ]);
  const safePage = Math.min(page, totalPages);

  const rowsNeededForPage = safePage * DEFAULT_LIST_PAGE_SIZE;
  const needsMoreRowsForPage = filteredCount < rowsNeededForPage;
  const canFetchMoreRows = listHasMore && !listFetchingMore;

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * DEFAULT_LIST_PAGE_SIZE;
    return displaySorted.slice(start, start + DEFAULT_LIST_PAGE_SIZE);
  }, [displaySorted, safePage]);

  useApplicationWatchSetRegistration(
    pageRows,
    !!selectedCompany?.id && !isArchiveView,
  );

  const awaitingPageData =
    pageRows.length === 0
    && needsMoreRowsForPage
    && listQuerySettled
    && (canFetchMoreRows || listFetchingMore);

  const pageTableLoading =
    (listInitialLoad && filteredCount === 0)
    || awaitingPageData;

  const listLoading = baseListLoading || pageTableLoading;
  const paginationBusy = needsMoreRowsForPage && listFetchingMore;

  useEffect(() => {
    if (!canFetchMoreRows) return;
    if (listQuery.isPending && apiPagesLoaded === 0) return;

    if (apiPagesLoaded < COMPANY_APPLICATIONS_INITIAL_PREFETCH_PAGES) {
      void listQuery.fetchNextPage();
      return;
    }

    const prefetchAhead = DEFAULT_LIST_PAGE_SIZE;
    if (filteredCount < rowsNeededForPage + prefetchAhead) {
      void listQuery.fetchNextPage();
    }
  }, [
    filteredCount,
    rowsNeededForPage,
    canFetchMoreRows,
    listQuery,
    apiPagesLoaded,
  ]);

  // Jumping ahead via Next / restored ?page= must pull enough server pages.
  useEffect(() => {
    if (!canFetchMoreRows || !needsMoreRowsForPage) return;
    void listQuery.fetchNextPage();
  }, [page, canFetchMoreRows, needsMoreRowsForPage, listQuery]);

  useEffect(() => {
    setPage(1);
  }, [listScope.archived, listScope.state, listScope.jobPositionId, listScope.orderBy]);

  const tableShowLoading =
    pageRows.length === 0 && (baseListLoading || pageTableLoading);

  const tableEmptyMessage = needsMoreRowsForPage && !listHasMore && !listFetchingMore
    ? t('applications.loadingMore')
    : t('applications.noApplications');

  const {
    clearSelection,
    selectAllOnPage,
    deselectAllOnPage,
    handleRowSelect,
  } = useShiftClickSelection(pageRows, initialSlice.selectedIds, {
    selectedIds,
    setSelectedIds,
  });

  useEffect(() => {
    if (!selectProcessableIntentRef.current) {
      selectReadyHandledRef.current = false;
      return;
    }
    if (selectReadyHandledRef.current || baseListLoading || isArchiveView || !billingFetched) return;

    const desiredStatus = searchParams.get('status') ?? statusFilter;
    if (desiredStatus !== 'all' && statusFilter !== desiredStatus) return;
    if (page !== 1) {
      setPage(1);
      setSearchParams((prev) => applyListPageToSearchParams(prev, 1), { replace: true });
      return;
    }

    const toSelect = getAutoSelectableApplications(
      pageRows,
      hasProcessingCredits,
      desiredStatus,
      { isRowProcessing: processQueue.isInProcessingFilter },
    );

    if (toSelect.length === 0) return;

    selectReadyHandledRef.current = true;
    selectProcessableIntentRef.current = false;
    setSelectedIds(new Set(toSelect.map(a => a.id)));

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('selectReady');
    const nextSearch = nextParams.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
      { replace: true, state: null },
    );
    toast.info(t('applications.bulkReadySelected', { count: toSelect.length }));
  }, [
    searchParams,
    statusFilter,
    pageRows,
    baseListLoading,
    isArchiveView,
    billingFetched,
    hasProcessingCredits,
    processQueue.isInProcessingFilter,
    page,
    setSelectedIds,
    setPage,
    setSearchParams,
    navigate,
    location.pathname,
    t,
  ]);

  const shiftKeyRef = useRef(false);

  const persistedListState = useMemo(() => {
    const currentSlice = captureQueueSlice({
      page,
      pinnedStatus,
      sortColumn: sort.column ?? queueSortDefaults.column,
      sortDirection: (sort.direction ?? queueSortDefaults.direction) as 'asc' | 'desc',
      selectedIds,
    });
    queueSlicesRef.current[queueView] = currentSlice;
    return {
      queueView,
      statusFilter,
      positionFilter,
      teamFilter,
      search,
      listSortVersion: APPLICATIONS_LIST_SORT_VERSION,
      active: queueSlicesRef.current.active,
      archive: queueSlicesRef.current.archive,
    };
  }, [
    queueView,
    statusFilter,
    positionFilter,
    teamFilter,
    search,
    page,
    pinnedStatus,
    sort.column,
    sort.direction,
    queueSortDefaults.column,
    queueSortDefaults.direction,
    selectedIds,
  ]);

  usePersistedListView(listPersistKey, persistedListState);

  const pruneSelectionQueueRef = useRef(queueView);
  useEffect(() => {
    if (pruneSelectionQueueRef.current !== queueView) {
      pruneSelectionQueueRef.current = queueView;
      return;
    }
    if (listApplications.length === 0) return;
    const valid = new Set(listApplications.map(a => a.id));
    setSelectedIds(prev => {
      const next = new Set([...prev].filter(id => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [listApplications, queueView, setSelectedIds]);

  useSkipInitialEffect(() => {
    setPage(1);
    setSearchParams((prev) => applyListPageToSearchParams(prev, 1), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset page only when list filters change
  }, [statusFilter, positionFilter, teamFilter, search]);

  // Clamp only after the list has loaded — while loading totalPages is 1 and would wipe a restored page.
  useEffect(() => {
    if (listLoading || paginationTotal === 0) return;
    if (page > totalPages) {
      setPage(totalPages);
      setSearchParams((prev) => applyListPageToSearchParams(prev, totalPages), { replace: true });
    }
  }, [page, totalPages, listLoading, paginationTotal, setSearchParams]);

  const billingPipelineErrorCount = useMemo(
    () => loadedApplications.filter(
      a => a.state === 'PIPELINE_ERROR' && a.lastFailureReason === 'billing_limit_reached',
    ).length,
    [loadedApplications],
  );

  const billingRetry = Math.max(billingPipelineErrorCount, storeBillingRetry);
  const patchDeferredCounts = usePendingBillingStore(s => s.patchDeferredCounts);
  useEffect(() => {
    patchDeferredCounts({ billingRetry });
  }, [billingRetry, patchDeferredCounts]);

  /**
   * handleBulkReject — fan out api.applications.reject across the
   * selected ids, settling all promises so partial failures don't abort
   * the whole batch. The BE rejects each application via DecisionService
   * which already handles cap checks and email dispatch (S5.A); this
   * client-side fan-out is a pragmatic shortcut while we don't have a
   * dedicated bulk endpoint.
   */
  const handleBulkReject = useCallback(async () => {
    if (!guardWrite()) return;
    if (!selectedCompany || selectedIds.size === 0) return;
    setBulkActionRunning(true);
    const ids = [...selectedIds];
    const results = await Promise.allSettled(
      ids.map(id => {
        const app = listApplications.find(a => a.id === id);
        if (!app) return Promise.reject(new Error('not_found'));
        return api.applications.reject(
          selectedCompany.id,
          app.positionId,
          id,
          bulkRejectNote || undefined,
          bulkRejectSendEmail,
        );
      }),
    );
    const ok = results.filter(r => r.status === 'fulfilled').length;
    const fail = results.length - ok;
    if (ok > 0) toast.success(t('applications.bulkRejectSuccess', { count: ok }));
    if (fail > 0) toast.error(t('applications.bulkRejectPartial', { count: fail }));
    setBulkRejectOpen(false);
    setBulkRejectNote('');
    setBulkRejectSendEmail(false);
    await refetchApplications();
    setBulkActionRunning(false);
  }, [guardWrite, selectedCompany, selectedIds, listApplications, bulkRejectNote, bulkRejectSendEmail, refetchApplications, t]);

  const selectedApps = useMemo(
    () => [...selectedIds]
      .map(id => listApplications.find(a => a.id === id))
      .filter((app): app is Application => !!app),
    [selectedIds, listApplications],
  );

  const scheduleArchiveInvalidation = useCallback(
    (companyId: string, restoredToActive = false) => {
      scheduleApplicationArchiveInvalidation(queryClient, companyId, restoredToActive);
    },
    [queryClient],
  );

  /** @deprecated Renamed to scheduleArchiveInvalidation — kept so stale hook deps cannot crash render. */
  const invalidateArchiveQueries = scheduleArchiveInvalidation;

  const handleBulkArchive = useCallback(async () => {
    if (!guardWrite()) return;
    if (!selectedCompany || selectedIds.size === 0) return;
    setBulkActionRunning(true);
    try {
      const result = await api.applications.bulkArchive(
        selectedCompany.id,
        toBulkArchivePayload(selectedApps),
      );
      if (result.updated > 0) {
        applyBulkArchiveToCache(queryClient, selectedCompany.id, selectedApps);
        toast.success(t('applications.bulkArchiveSuccess', { count: result.updated }));
      }
      if (result.errors > 0) {
        toast.error(t('applications.bulkArchivePartial', { count: result.errors }));
      }
      setSelectedIds(new Set());
      scheduleArchiveInvalidation(selectedCompany.id);
    } finally {
      setBulkActionRunning(false);
    }
  }, [
    guardWrite,
    selectedCompany,
    selectedIds.size,
    selectedApps,
    queryClient,
    scheduleArchiveInvalidation,
    t,
  ]);

  const handleBulkUnarchive = useCallback(async () => {
    if (!guardWrite()) return;
    if (!selectedCompany || selectedIds.size === 0) return;
    setBulkActionRunning(true);
    try {
      const result = await api.applications.bulkUnarchive(
        selectedCompany.id,
        toBulkArchivePayload(selectedApps),
      );
      if (result.updated > 0) {
        applyBulkUnarchiveToCache(queryClient, selectedCompany.id, selectedApps);
        toast.success(t('applications.bulkUnarchiveSuccess', { count: result.updated }));
      }
      if (result.errors > 0) {
        toast.error(t('applications.bulkUnarchivePartial', { count: result.errors }));
      }
      if (result.updated === 0 && result.errors > 0) {
        toast.error(t('applications.bulkUnarchiveUnavailable'));
      }
      setSelectedIds(new Set());
      scheduleArchiveInvalidation(selectedCompany.id, true);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message;
      toast.error(msg || t('applications.bulkUnarchiveUnavailable'));
    } finally {
      setBulkActionRunning(false);
    }
  }, [
    guardWrite,
    selectedCompany,
    selectedIds.size,
    selectedApps,
    queryClient,
    scheduleArchiveInvalidation,
    t,
  ]);

  const bulkSelectionBreakdown = useMemo(
    () => getBulkProcessBreakdown(selectedApps, hasProcessingCredits),
    [selectedApps, hasProcessingCredits],
  );

  const selectedProcessableCount = bulkProcessableCount(bulkSelectionBreakdown);
  const selectedStopCount = processQueue.countSelectedStopEligible(selectedApps);
  const bulkProcessButton = getBulkProcessButtonState(bulkSelectionBreakdown, hasProcessingCredits);

  const handleBulkStopProcessing = useCallback(() => {
    if (!guardWrite()) return;
    if (!selectedCompany || selectedStopCount === 0) return;
    const stopTargets = selectedApps.filter((a) => processQueue.isExplicitStopEligible(a.id));
    void processQueue.runBulkAbortStuck(stopTargets, { mode: 'explicit' });
  }, [guardWrite, selectedCompany, selectedStopCount, selectedApps, processQueue]);

  const handleBulkProcess = useCallback(() => {
    if (!guardWrite()) return;
    if (!selectedCompany || selectedProcessableCount === 0 || !hasProcessingCredits) return;
    const processable = getSelectedProcessableApps(selectedIds, loadedApplications, hasProcessingCredits);
    const companyId = selectedCompany.id;

    setBulkProcessConfirmOpen(false);

    // Queue + banner first; stay on current status filter — updatedAt sort surfaces in-flight rows.
    processQueue.prepareBulkProcess(processable);
    setPage(1);
    setSearchParams(
      (prev) => applyListPageToSearchParams(prev, 1),
      { replace: true },
    );

    void (async () => {
      const result = await processQueue.runBulkProcess(processable, { skipPrepare: true });
      if (result?.skippedBilling > 0 && result.processed === 0) {
        setStatusFilter('PENDING_UPGRADE');
        setPage(1);
        setSearchParams(
          (prev) => applyStatusToSearchParams(applyListPageToSearchParams(prev, 1), 'PENDING_UPGRADE'),
          { replace: true },
        );
      }
      const unlocked = await api.applications.unlockDeferred(companyId);
      setDeferredCounts(
        reconcileDeferredCountsWithApplications(queryClient, companyId, hasProcessingCredits, {
          pendingUpgrade: unlocked.pendingUpgrade,
          readyToProcess: unlocked.readyToProcess,
          billingRetry: unlocked.billingRetry,
        }),
      );
      if (unlocked.unlocked > 0) {
        patchUnlockedDeferredApplications(queryClient, companyId);
      }
      emitBillingUsageChanged();
    })();
  }, [
    guardWrite,
    selectedCompany,
    selectedProcessableCount,
    hasProcessingCredits,
    selectedIds,
    loadedApplications,
    processQueue,
    setDeferredCounts,
    queryClient,
  ]);

  // Pre-onboarding short-circuit: same pattern as JobPositionsPage. With
  // no company, every filter / table cell below is meaningless — render
  // the unified CTA instead so the user is steered to onboarding.
  if (!selectedCompany) {
    return (
      <div className="space-y-4">
        <NoCompanyEmptyState body={t('emptyState.noCompany.body.applications')} />
      </div>
    );
  }

  // Sprint tables_responsive_v1 — column schema dichiarativo.
  // Note: checkbox row select è gestito separatamente nei card via
  // `renderCardHeader` (priority='hidden' per non comparire nel body
  // card) e come `selection` column su tablet/desktop.
  const dimAwaitingUpgrade = (row: AppRow) =>
    isApplicationAwaitingUpgrade(row, hasProcessingCredits) ? 'opacity-60' : undefined;

  const applicationRowClassName = (row: AppRow) =>
    cn(
      isApplicationAwaitingUpgrade(row, hasProcessingCredits) && 'bg-amber-50/30 dark:bg-amber-950/10',
      isApplicationReadyToProcess(row, hasProcessingCredits) && 'bg-muted/20',
    );

  const applicationsColumns: ResponsiveTableColumn<AppRow>[] = [
    {
      id: 'selection',
      label: '',
      priority: 'hidden',
      alwaysVisible: true,
      sortable: false,
      ...TABLE_COL.checkbox,
      renderHeader: () => (
        <SelectAllHeaderCheckbox
          pageRows={pageRows}
          selectedIds={selectedIds}
          selectAllOnPage={selectAllOnPage}
          deselectAllOnPage={deselectAllOnPage}
          clearSelection={clearSelection}
          ariaLabel={t('applications.bulkSelectAll')}
        />
      ),
      render: (row) => {
        const index = pageRows.findIndex((r) => r.id === row.id);
        return (
          <input
            type="checkbox"
            className="h-3.5 w-3.5 rounded border-input accent-primary cursor-pointer block mx-auto"
            checked={selectedIds.has(row.id)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => { shiftKeyRef.current = e.shiftKey; }}
            onChange={() => { handleRowSelect(index, row.id, shiftKeyRef.current); }}
            aria-label={`Select ${row.candidateFirstName} ${row.candidateLastName}`}
          />
        );
      },
    },
    {
      id: 'candidateFirstName',
      label: t('applications.candidate'),
      priority: 'primary',
      alwaysVisible: true,
      ...TABLE_COL.title,
      sortable: true,
      truncate: true,
      render: (row) => (
        <div className={cn('flex items-center gap-1.5 min-w-0', dimAwaitingUpgrade(row))}>
          {row.hasIdentityMismatch && (
            <IdentityMismatchBadge mismatch={row.identityMismatch} variant="icon" className="shrink-0" />
          )}
          <span
            className="font-medium truncate block min-w-0"
            title={`${row.candidateFirstName} ${row.candidateLastName}`}
          >
            {row.candidateFirstName} {row.candidateLastName}
          </span>
        </div>
      ),
    },
    {
      id: 'candidateEmail',
      label: t('applications.email'),
      priority: 'secondary',
      hideOn: 'mobile',
      ...TABLE_COL.text,
      truncate: true, // email può essere molto lunga + crearebbe row alta — keep truncate
      sortable: true,
      defaultVisible: false,
      render: (row) => (
        <span
          className={cn('text-muted-foreground truncate block', dimAwaitingUpgrade(row))}
          title={row.candidateEmail}
        >
          {row.candidateEmail}
        </span>
      ),
    },
    {
      id: 'positionTitle',
      label: t('applications.position'),
      priority: 'secondary',
      ...TABLE_COL.subtitle,
      sortable: true,
      truncate: true,
      render: (row) => (
        <span
          className={cn('text-muted-foreground truncate block', dimAwaitingUpgrade(row))}
          title={row.positionTitle}
        >
          {row.positionTitle}
        </span>
      ),
    },
    {
      id: 'teamName',
      label: t('applications.team'),
      priority: 'tertiary',
      ...TABLE_COL.text,
      sortable: true,
      truncate: true,
      defaultVisible: false,
      render: (row) => (
        <span
          className={cn('text-muted-foreground truncate block', dimAwaitingUpgrade(row))}
          title={row.teamName}
        >
          {row.teamName}
        </span>
      ),
    },
    {
      id: 'channel',
      label: t('applications.source'),
      priority: 'tertiary',
      ...TABLE_COL.channel,
      sortable: true,
      render: (row) => (
        <div className={cn(dimAwaitingUpgrade(row))}>
          <ChannelBadge channel={row.channel} />
        </div>
      ),
    },
    {
      id: 'appliedAt',
      label: t('applications.applied'),
      priority: 'tertiary',
      hideOn: 'mobile',
      ...TABLE_COL.date,
      sortable: true,
      render: (row) => (
        <span
          className={cn('text-muted-foreground', dimAwaitingUpgrade(row))}
          title={dateTimeTooltip(row.appliedAt)}
        >
          {formatRelative(row.appliedAt)}
        </span>
      ),
    },
    {
      id: 'updatedAt',
      label: t('applications.updated'),
      priority: 'tertiary',
      ...TABLE_COL.date,
      sortable: true,
      render: (row) => (
        <span
          className={cn('text-muted-foreground', dimAwaitingUpgrade(row))}
          title={dateTimeTooltip(row.updatedAt)}
        >
          {formatRelative(row.updatedAt)}
        </span>
      ),
    },
    {
      id: 'aiScore',
      label: t('applications.score'),
      priority: 'tertiary',
      sortable: true,
      ...TABLE_COL.score,
      render: (row) => (
        <div className={cn(dimAwaitingUpgrade(row))}>
          <ScoreBadge score={row.aiScore} />
        </div>
      ),
    },
    {
      id: 'state',
      label: t('applications.status'),
      priority: 'primary',
      alwaysVisible: true,
      ...TABLE_COL.status,
      onHeaderClick: cycleStatusPin,
      headerPin: true,
      render: (row) => (
        <div className={cn(statusCellClass(), dimAwaitingUpgrade(row))}>
          <ApplicationRowProcessBadge
            state={row.state}
            stopping={processQueue.isStopping(row.id)}
            processing={processQueue.showsActiveProcessingBadge(row.id)}
            queued={processQueue.showsQueuedInBulkBadge(row.id)}
            stuck={processQueue.showsProcessingStuckBadge(row.id)}
            hasProcessingCredits={hasProcessingCredits}
          />
        </div>
      ),
    },
    {
      id: 'interviewStatus',
      label: t('applications.interview'),
      priority: 'tertiary',
      alwaysVisible: true,
      ...TABLE_COL.interview,
      sortable: true,
      render: (row) => (
        <div className={cn(dimAwaitingUpgrade(row))}>
          <InterviewSubstatusBadges interviews={row.interviews} />
        </div>
      ),
    },
  ];

  return (
    <div
      className={cn(
        viewportLocked
          ? 'flex min-h-0 flex-1 flex-col gap-4 overflow-hidden'
          : 'space-y-4',
      )}
    >
      <ApplicationsListFiltersToolbar
        queue={{
          value: queueView,
          onChange: handleQueueViewChange,
          activeLabel: t('applications.queueActive'),
          archiveLabel: t('applications.queueArchive'),
          activeCount: activeApplicationsCount,
          activeCountLoading: activeTabCountLoading,
          activeCountHasMore: activeTabCountHasMore,
          archivedCount: archivedApplicationsCount,
          archivedCountHasMore: archivedTabCountHasMore,
        }}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: t('applications.searchCandidates'),
        }}
        status={{
          value: statusFilter,
          onChange: handleStatusFilterChange,
          active: statusFilter !== 'all',
          options: statusOptions,
        }}
        position={{
          value: positionFilter,
          onChange: setPositionFilter,
          active: positionFilter !== 'all',
          placeholder: t('applications.allPositions'),
          options: [
            { value: 'all', label: t('applications.allPositions') },
            ...activePositions.map((p) => ({ value: p.id, label: p.title })),
          ],
        }}
        team={{
          value: teamFilter,
          onChange: setTeamFilter,
          active: teamFilter !== 'all',
          placeholder: t('applications.allTeams'),
          options: [
            { value: 'all', label: t('applications.allTeams') },
            { value: 'unassigned', label: t('positions.noTeam') },
            ...companyTeams.map((team) => ({ value: team.id, label: team.name })),
          ],
        }}
        addCandidate={
          !isArchiveView
            ? {
                label: t('manualApply.cta'),
                disabled: writeBlocked,
                onClick: () => {
                  if (!guardWrite()) return;
                  setManualDialogOpen(true);
                },
              }
            : undefined
        }
        showClearFilters={hasActiveListFilters}
        onClearFilters={clearAllListFilters}
        clearFiltersLabel={t('applications.clearFilters')}
      />

      {!isArchiveView && (
        <ManualApplicationDialog
          open={manualDialogOpen}
          onOpenChange={setManualDialogOpen}
          companyId={selectedCompany.id}
          positions={positions}
          defaultPositionId={positionFilter !== 'all' ? positionFilter : undefined}
          onSubmitted={({ applicationId, positionId }) => {
            void appendManualApplication(positionId, applicationId);
          }}
        />
      )}

      {/* Sprint 5 (S5.D) — bulk action bar. Only renders when at least
          one row is selected; sits between the filter row and the table
          so it doesn't shift the table layout or cover any data. */}
      {selectedIds.size > 0 && (
        <ApplicationsBulkActionBar
          selectedCount={selectedIds.size}
          isArchiveView={isArchiveView}
          writeBlocked={writeBlocked}
          bulkActionRunning={bulkActionRunning}
          selectedStopCount={selectedStopCount}
          bulkProcessButton={bulkProcessButton}
          processQueueActiveCount={processQueue.activeCount}
          onBulkUnarchive={() => void handleBulkUnarchive()}
          onBulkStopProcessing={handleBulkStopProcessing}
          onBulkProcess={() => setBulkProcessConfirmOpen(true)}
          onBulkArchive={() => void handleBulkArchive()}
          onBulkReject={() => setBulkRejectOpen(true)}
          onClearSelection={clearSelection}
          guardWrite={guardWrite}
        />
      )}

      {/* ResponsiveTable: scroll orizzontale su mobile/tablet, tabella full su desktop. */}
      <ListTablePanel
        scrollableBody={viewportLocked}
        fillViewport={viewportLocked}
        className={viewportLocked ? 'flex-1 min-h-0' : undefined}
        showFooter={paginationTotal > 0}
        footer={
          <ListPagination
            page={safePage}
            totalPages={totalPages}
            totalItems={paginationTotal}
            onPageChange={handlePageChange}
            disabled={listLoading || paginationBusy}
            totalHasMore={
              listHasMore
              && (useFilteredCountApi
                ? filteredCountFromApi == null
                : (isArchiveView ? archivedCountFromApi == null : activeCountFromApi == null))
            }
            centerHint={
              listFetchingMore
                ? t('applications.hydratingList', {
                  loaded: loadedApplications.length,
                  total: loadedApplications.length,
                })
                : undefined
            }
          />
        }
      >
        <ResponsiveTable<AppRow>
            scrollBodyOnly={viewportLocked}
            className={viewportLocked ? 'h-full min-h-0' : undefined}
            data={pageRows}
            columns={applicationsColumns}
            getRowKey={(row) => row.id}
            onRowClick={openApplicationDetail}
            renderCardHeader={(row) => (
              <div className={cn('flex items-start gap-2.5', dimAwaitingUpgrade(row))}>
                <input
                  type="checkbox"
                  className="mt-1 h-3.5 w-3.5 shrink-0 rounded border-input accent-primary cursor-pointer"
                  checked={selectedIds.has(row.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    e.stopPropagation();
                    const idx = pageRows.findIndex((r) => r.id === row.id);
                    if (idx >= 0) handleRowSelect(idx, row.id, false);
                  }}
                  aria-label={`Select ${row.candidateFirstName} ${row.candidateLastName}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-foreground text-[15px] leading-tight flex items-center gap-1.5 min-w-0">
                    {row.hasIdentityMismatch && (
                      <IdentityMismatchBadge mismatch={row.identityMismatch} variant="icon" className="shrink-0" />
                    )}
                    <span className="truncate">
                      {row.candidateFirstName} {row.candidateLastName}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">
                    {row.candidateEmail}
                  </div>
                </div>
                <ScoreBadge score={row.aiScore} />
              </div>
            )}
            rowClassName={applicationRowClassName}
            sort={sort}
            onSortChange={handleColumnSort}
            isLoading={tableShowLoading}
            emptyState={
              <p className="text-sm text-muted-foreground">{tableEmptyMessage}</p>
            }
            storageKey="applications-list-v2"
            ariaLabel={t('applications.candidate')}
            fixedPageSize={undefined}
          />
      </ListTablePanel>

      {/* Sprint 5 (S5.D) — bulk reject confirmation. Mirrors the per-row
          reject dialog UX (note + sendEmail checkbox) so muscle memory
          carries over. The note + opt-in apply to ALL selected rows. */}
      <Dialog open={bulkRejectOpen} onOpenChange={setBulkRejectOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {t('applications.bulkRejectTitle', { count: selectedIds.size })}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t('applications.bulkRejectDesc')}</p>
          <Textarea
            placeholder={t('appDetail.rejectNote')}
            value={bulkRejectNote}
            onChange={(e) => setBulkRejectNote(e.target.value)}
            rows={3}
          />
          <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              className="mt-0.5 h-3.5 w-3.5 rounded border-input accent-primary"
              checked={bulkRejectSendEmail}
              onChange={(e) => setBulkRejectSendEmail(e.target.checked)}
            />
            <span>
              {t('appDetail.rejectSendEmail')}
              <span className="block text-[11px] text-muted-foreground/80">
                {t('appDetail.rejectSendEmailHint')}
              </span>
            </span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkRejectOpen(false)} disabled={bulkActionRunning}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => { void handleBulkReject(); }}
              disabled={bulkActionRunning}
            >
              {bulkActionRunning ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              {t('applications.bulkRejectConfirm', { count: selectedIds.size })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProcessPendingConfirmDialog
        open={bulkProcessConfirmOpen}
        onOpenChange={setBulkProcessConfirmOpen}
        breakdown={bulkSelectionBreakdown}
        busy={false}
        variant="bulk"
        onConfirm={handleBulkProcess}
      />

      <FloatingAiChat
        open={chatOpen}
        onOpenChange={setChatOpen}
        expanded={chatExpanded}
        onExpandedChange={setChatExpanded}
        messages={chatMessages}
        loading={chatLoading}
        statusMessages={chatStreamStatuses}
        input={chatInput}
        onInputChange={setChatInput}
        onSend={(prompt) => sendChat(
          prompt,
          { status: statusFilter, positionId: positionFilter, teamId: teamFilter, search },
          loadedApplications,
        )}
        onReset={resetChat}
        contextLabel={t('aiChat.applications.contextLabel')}
        placeholder={t('aiChat.applications.placeholder')}
        suggestedPrompts={[
          t('aiChat.applications.prompt1'),
          t('aiChat.applications.prompt2'),
          t('aiChat.applications.prompt3'),
          t('aiChat.applications.prompt4'),
        ]}
      />
    </div>
  );
};

export default ApplicationsPage;
